import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ChevronRight, Download, BookOpen, Flame, Pentagon, Type, Leaf } from 'lucide-react';
import SEO from '../components/SEO';
import { API_URL } from '../utils/api';

const LOGO_URL = 'https://storage.stikes-hi.ac.id/stikes-hi-media/3fbebd76-22d3-4641-a12f-ec3c51280150.png';

const PageHero = ({ title, breadcrumbs }) => (
  <div className="page-hero pt-[70px]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 relative z-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="flex items-center gap-2 text-white/70 text-sm mb-4">
          <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
            <Home size={14} /> Beranda
          </Link>
          {breadcrumbs.map((bc, i) => (
            <React.Fragment key={i}>
              <ChevronRight size={14} />
              <span className={i === breadcrumbs.length - 1 ? 'text-white' : ''}>{bc}</span>
            </React.Fragment>
          ))}
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-white font-heading">{title}</h1>
      </motion.div>
    </div>
  </div>
);

const filosofiItems = [
  { icon: <Pentagon size={22} />, title: 'Bentuk Segi Lima', desc: 'Melambangkan Pancasila sebagai dasar negara dan landasan moral dalam pengembangan pendidikan kesehatan Indonesia.' },
  { icon: <BookOpen size={22} />, title: 'Buku Terbuka', desc: 'Melambangkan ilmu pengetahuan, keterbukaan dalam belajar, dan komitmen terhadap pendidikan tinggi yang berkelanjutan.' },
  { icon: <Flame size={22} />, title: 'Api/Obor Semangat', desc: 'Melambangkan semangat, dedikasi, dan cahaya pengetahuan yang menerangi jalan para generasi muda dalam bidang kesehatan.' },
  { icon: <Type size={22} />, title: 'Siluet "HI"', desc: 'Identitas unik STIKES Harapan Ibu yang terintegrasi dalam desain logo, mencerminkan nama dan jati diri institusi.' },
  { icon: <Leaf size={22} />, title: 'Warna Hijau', desc: 'Melambangkan kesehatan, pertumbuhan, harapan, dan vitalitas yang menjadi inti dari misi pendidikan kesehatan.' },
];

const brandColors = [
  { hex: '#16A34A', name: 'Hijau Kesehatan', desc: 'Warna utama logo' },
  { hex: '#7B2FBE', name: 'Ungu Inovasi', desc: 'Warna brand institusi' },
  { hex: '#FFFFFF', name: 'Putih Kejujuran', desc: 'Warna netral & kesucian', border: true },
];

const prodiColors = [
  { hex: '#E1E100', name: 'S1 Kesmas' },
  { hex: '#006600', name: 'S1 Keperawatan' },
  { hex: '#0000CA', name: 'S1 Farmasi' },
  { hex: '#800000', name: 'Profesi Ners' },
  { hex: '#6B7280', name: 'Profesi Apoteker' },
];

/// Tombol "Unduh Logo Pack": tautannya diatur dari panel admin (Halaman Logo)
/// dan dibuka di tab baru. Tanpa tautan, tombol tidak ditampilkan.
const LogoPackDownload = () => {
  const [setting, setSetting] = useState(null);

  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/api/public/logo-page`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (active) setSetting(data); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  if (!setting?.downloadUrl) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="text-center mt-12"
    >
      <a
        href={setting.downloadUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="download-logo-btn"
        className="inline-flex items-center gap-3 bg-gradient-to-r from-[#7B2FBE] to-[#9333EA]
          text-white px-8 py-4 rounded-full font-semibold text-sm hover:opacity-90
          transition-all duration-300 hover:scale-105 shadow-lg shadow-purple-500/30"
      >
        <Download size={18} />
        Unduh Logo Pack
      </a>
      {setting.downloadNote && <p className="text-xs text-gray-400 mt-3">{setting.downloadNote}</p>}
    </motion.div>
  );
};

const LogoKampus = () => (
  <div data-testid="logo-kampus-page">
    <SEO 
      title="Logo & Identitas Visual Official"
      description="Download logo resmi STIKES Harapan Ibu Jambi (STIKES HI) dalam format resolusi tinggi. Pelajari filosofi lambang, identitas visual, dan standar warna brand kami."
      image={LOGO_URL}
    />
    
    <PageHero title="Logo & Identitas Visual" breadcrumbs={['Tentang Kami', 'Logo Kampus']} />

    <div className="bg-[#F8F7FF] dark:bg-[#121020]">
      {/* Logo Showcase */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl font-bold text-[#1A1A2E] dark:text-white font-heading">Logo Resmi Kampus</h2>
            <p className="text-gray-500 text-sm mt-2">Identitas visual utama Sekolah Tinggi Ilmu Kesehatan Harapan Ibu Jambi</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Light background */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              data-testid="logo-light-card"
              className="bg-white rounded-3xl p-12 flex flex-col items-center border border-gray-200 shadow-sm"
            >
              <img 
                src={LOGO_URL} 
                alt="Logo Resmi STIKES Harapan Ibu Jambi (STIKES HI) - Versi Background Terang" 
                title="Logo Resmi STIKES Harapan Ibu Jambi"
                className="h-40 w-40 object-contain mb-4" 
              />
              <p className="text-sm text-gray-500 font-medium whitespace-nowrap">Logo Official (Varian Latar Terang)</p>
            </motion.div>

            {/* Dark background */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              data-testid="logo-dark-card"
              className="bg-[#0A0A0F] rounded-3xl p-12 flex flex-col items-center border border-gray-800 shadow-sm"
            >
              <img 
                src={LOGO_URL} 
                alt="Logo Resmi STIKES Harapan Ibu Jambi (STIKES HI) - Versi Background Gelap" 
                title="Logo STIKES HI PNG High Resolution"
                className="h-40 w-40 object-contain mb-4" 
              />
              <p className="text-sm text-gray-400 font-medium whitespace-nowrap">Logo Official (Varian Latar Gelap)</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Decorative & Artistic Assets */}
      <section className="py-16 bg-white dark:bg-[#100E1C] border-y border-purple-50 dark:border-purple-900/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-[#1A1A2E] dark:text-white font-heading">Aset Dekoratif & Header</h2>
            <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm max-w-2xl mx-auto leading-relaxed">
              Logo dengan sentuhan seni dan motif kaligrafi yang dirancang khusus untuk penggunaan dekoratif, header surat, maupun watermark dokumen resmi guna memberikan kesan elegan dan premium.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-[#F8F7FF] dark:bg-[#1A182E] rounded-3xl p-6 border border-purple-100 dark:border-purple-900/20 shadow-sm overflow-hidden group"
            >
              <div className="aspect-[21/9] flex items-center justify-center mb-5 bg-white dark:bg-black/20 rounded-2xl p-4 overflow-hidden">
                <img 
                  src="https://storage.stikes-hi.ac.id/stikes-hi-media/c1b346f3-10da-40f1-9d5d-ed71d24a3899.png" 
                  alt="Aset Header Artistik STIKES Harapan Ibu Jambi - Motif Klasik untuk Dokumen Resmi" 
                  title="Header Artistik STIKES HI Jambi"
                  className="max-h-full w-auto object-contain transition-transform duration-500 group-hover:scale-110" 
                />
              </div>
              <div className="text-center">
                <p className="text-sm font-bold text-[#1A1A2E] dark:text-gray-200">Artistic Logo - Motif Header A</p>
                <span className="text-[10px] text-gray-400 uppercase tracking-widest mt-1 block">Rasio 21:9 | High Resolution</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-[#F8F7FF] dark:bg-[#1A182E] rounded-3xl p-6 border border-purple-100 dark:border-purple-900/20 shadow-sm overflow-hidden group"
            >
              <div className="aspect-[21/9] flex items-center justify-center mb-5 bg-white dark:bg-black/20 rounded-2xl p-4 overflow-hidden">
                <img 
                  src="https://storage.stikes-hi.ac.id/stikes-hi-media/9b96d9be-5356-4d6d-abc6-981b44edb79a.png" 
                  alt="Aset Watermark Artistik STIKES Harapan Ibu Jambi - Elemen Dekoratif Identitas Visual" 
                  title="Watermark Artistik STIKES HI"
                  className="max-h-full w-auto object-contain transition-transform duration-500 group-hover:scale-110" 
                />
              </div>
              <div className="text-center">
                <p className="text-sm font-bold text-[#1A1A2E] dark:text-gray-200">Artistic Logo - Motif Header B</p>
                <span className="text-[10px] text-gray-400 uppercase tracking-widest mt-1 block">Rasio 21:9 | Transparent Watermark</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Filosofi */}
      <section className="py-16 bg-white dark:bg-[#100E1C]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-[#1A1A2E] dark:text-white font-heading">Filosofi Lambang</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filosofiItems.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`filosofi-card-${i}`}
                className="p-5 rounded-2xl bg-[#F8F7FF] dark:bg-[#1A182E] border border-purple-100 dark:border-purple-900/20 hover:border-[#7B2FBE]/40 transition-all"
              >
                <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center text-[#7B2FBE] mb-3">
                  {item.icon}
                </div>
                <h3 className="font-bold text-[#1A1A2E] dark:text-white text-sm mb-2 font-heading">{item.title}</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Warna Identitas */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-[#1A1A2E] dark:text-white font-heading">Warna Identitas</h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
            {brandColors.map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                data-testid={`color-swatch-${i}`}
                className="rounded-2xl overflow-hidden shadow-sm border border-gray-100 dark:border-gray-800"
              >
                <div className="h-28" style={{ backgroundColor: c.hex, border: c.border ? '2px solid #E5E7EB' : 'none' }} />
                <div className="p-4 bg-white dark:bg-[#1A182E]">
                  <div className="font-mono text-sm font-bold text-[#1A1A2E] dark:text-white">{c.hex}</div>
                  <div className="text-sm font-semibold text-[#1A1A2E] dark:text-gray-200 font-heading">{c.name}</div>
                  <div className="text-xs text-gray-400">{c.desc}</div>
                </div>
              </motion.div>
            ))}
          </div>

          <h3 className="text-xl font-bold text-[#1A1A2E] dark:text-white font-heading mb-6 text-center">Warna Program Studi</h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            {prodiColors.map((c, i) => (
              <div key={i} className="rounded-xl overflow-hidden border border-gray-100 dark:border-gray-800 shadow-sm">
                <div className="h-14" style={{ backgroundColor: c.hex }} />
                <div className="p-2 bg-white dark:bg-[#1A182E] text-center">
                  <div className="font-mono text-xs text-gray-500 dark:text-gray-400">{c.hex}</div>
                  <div className="text-xs font-medium text-[#1A1A2E] dark:text-gray-200">{c.name}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Download Button */}
          <LogoPackDownload />
        </div>
      </section>
    </div>
  </div>
);

export default LogoKampus;
